import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get, ref } from 'firebase/database';
import { db } from '../../config/firebase';
import {
  getBranchFromCourses,
  getStudentDisplayName,
  resolveActiveCourseContexts,
  resolvePortfolioCourses,
} from '../../utils/studentIdentity';

const toArray = (value) =>
  Object.entries(value || {}).map(([id, item]) => ({
    id,
    ...(item || {}),
  }));

const StudentQrResolverPage = () => {
  const navigate = useNavigate();
  const { academyStudentId } = useParams();
  const [state, setState] = useState({
    title: 'Checking student pass',
    message: 'Please wait while we open the correct page.',
    student: null,
    branch: '',
  });

  useEffect(() => {
    const resolveRoute = async () => {
      try {
        const [studentsSnapshot, usersSnapshot, enrollmentsSnapshot, coursesSnapshot] =
          await Promise.all([
            get(ref(db, 'students')),
            get(ref(db, 'users')),
            get(ref(db, 'enrollments')),
            get(ref(db, 'courses')),
          ]);

        const students = studentsSnapshot.val() || {};
        const users = usersSnapshot.val() || {};
        const courses = coursesSnapshot.val() || {};
        const studentEntry = Object.entries(students).find(
          ([, student]) => student?.academyStudentId === academyStudentId,
        );

        if (!studentEntry) {
          setState({
            title: 'Student pass not found',
            message: 'This QR code does not match a student record.',
            student: null,
            branch: '',
          });
          return;
        }

        const [studentId, student] = studentEntry;
        const user = users[studentId] || {};
        const enrollments = toArray(enrollmentsSnapshot.val()).filter(
          (item) => item.studentId === studentId,
        );
        const activeCourses = resolveActiveCourseContexts({
          enrollments,
          courses,
        });
        const completedCourses = resolvePortfolioCourses({
          enrollments,
          courses,
        });

        if (activeCourses.length) {
          navigate(`/reception/kiosk/${academyStudentId}`, { replace: true });
          return;
        }

        if (completedCourses.length) {
          navigate(`/students/portfolio/${academyStudentId}`, { replace: true });
          return;
        }

        setState({
          title: 'Student record found',
          message:
            'This student is not in an active class right now. Please ask reception for help if you need attendance or payment support.',
          student: getStudentDisplayName(user, student),
          branch: getBranchFromCourses({
            student,
            enrollments,
            courses,
          }),
        });
      } catch (error) {
        console.error('Failed to resolve student QR:', error);
        setState({
          title: 'Unable to open student pass',
          message: 'Please try again or ask reception for help.',
          student: null,
          branch: '',
        });
      }
    };

    resolveRoute();
  }, [academyStudentId, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-stone-100 px-4 py-10">
      <div className="w-full max-w-lg rounded-[32px] border border-stone-200 bg-white px-6 py-8 text-center shadow-[0_24px_60px_-40px_rgba(28,25,23,0.35)]">
        <img
          src="/logo.png"
          alt="Brissbella"
          className="mx-auto h-14 w-auto object-contain"
        />
        <h1 className="mt-6 text-3xl font-semibold tracking-tight text-stone-950">
          {state.title}
        </h1>
        <p className="mt-3 text-sm leading-6 text-stone-600">{state.message}</p>
        {state.student ? (
          <div className="mt-6 rounded-[24px] border border-stone-200 bg-stone-50 px-5 py-5 text-left">
            <p className="text-xs font-medium text-stone-500">Student</p>
            <p className="mt-1 text-lg font-semibold text-stone-950">{state.student}</p>
            <p className="mt-3 text-xs font-medium text-stone-500">Branch</p>
            <p className="mt-1 text-sm font-semibold text-stone-900">
              {state.branch || 'Not set'}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StudentQrResolverPage;
